// frontend/src/components/dashboard/pages/profilesetting/dialogs/ViewProfile.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
  Divider,
  useTheme,
  Chip,
  Tooltip,
  Card,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  Email as EmailIcon,
  Place,
  VerifiedUser as VerifiedUserIcon,
  Bookmark as BookmarkIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { profileStyles, instagramColors } from '../styles/profileStyles';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';

// Define motion components
const MotionDiv = motion.div;

// Social Media Link Component
const SocialMediaLink = ({ url, icon, name, color, backgroundColor }) => {
  const theme = useTheme();
  const styles = profileStyles(theme);
  const isDarkMode = theme.palette.mode === 'dark';
  
  if (!url) return null;
  
  const Icon = icon;
  
  return (
    <MotionDiv
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 15 }}
    >
      <Tooltip title={name} placement="top">
        <IconButton 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          size="large"
          sx={{
            ...styles.socialMediaButton,
            color: color || theme.palette.common.white,
            backgroundColor: backgroundColor || (isDarkMode ? '#4a5568' : instagramColors.blue),
            boxShadow: isDarkMode 
              ? '0px 3px 10px rgba(0,0,0,0.3) !important'
              : '0px 3px 10px rgba(0,0,0,0.15) !important',
            '&:hover': {
              backgroundColor: backgroundColor 
                ? `${backgroundColor}dd !important` 
                : isDarkMode 
                  ? '#3a4454 !important'
                  : `${instagramColors.darkBlue} !important`,
              transform: 'translateY(-2px) !important',
              boxShadow: isDarkMode 
                ? '0px 5px 15px rgba(0,0,0,0.4) !important'
                : '0px 5px 15px rgba(0,0,0,0.2) !important',
            },
            width: '44px !important',
            height: '44px !important'
          }}
          aria-label={name}
        >
          <Icon fontSize="medium" />
        </IconButton>
      </Tooltip>
    </MotionDiv>
  );
};

// Main ViewProfile Component
const ViewProfile = ({ open, onClose, profileData }) => {
  const theme = useTheme();
  const styles = profileStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  const [copied, setCopied] = useState(false);
  
  // Handle copy profile URL
  const handleShareProfile = () => {
    const profileUrl = `${window.location.origin}/profile/${profileData?.username}`;
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Reset copied state when dialog closes
  useEffect(() => {
    if (!open) {
      setCopied(false);
    }
  }, [open]);

  if (!profileData) return null;
  
  // Theme-aware gradient background
  const profileGradient = isDarkMode 
    ? 'linear-gradient(45deg, #4a5568, #553c8b, #764ba2) !important'
    : 'linear-gradient(45deg, #FCAF45, #E1306C, #5851DB) !important';

  // Theme-aware colors
  const backgroundColor = isDarkMode ? '#1e1e1e' : '#ffffff';
  const textColor = isDarkMode ? '#ffffff' : '#262626';
  const secondaryTextColor = isDarkMode ? '#b3b3b3' : '#8E8E8E';
  const borderColor = isDarkMode ? '#404040' : '#EFEFEF';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...styles.profileCard,
          borderRadius: { xs: '0 !important', sm: '16px !important' },
          m: { xs: '0 !important', sm: '8px !important' },
          height: { xs: '100% !important', sm: 'auto !important' },
          maxHeight: '100vh !important',
          maxWidth: { sm: '500px !important' },
          overflow: 'hidden !important',
          boxShadow: isDarkMode 
            ? '0 10px 40px rgba(0, 0, 0, 0.5) !important'
            : '0 10px 40px rgba(0, 0, 0, 0.2) !important',
          background: `${backgroundColor} !important`,
        }
      }}
      sx={{
        backdropFilter: 'blur(5px) !important'
      }}
      onClick={(e) => {
        // Close dialog when clicking the backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Box>
        {/* Theme-aware header */}
        <Box
          sx={{
            ...styles.instagramHeader,
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'space-between !important',
            p: '10px !important',
            borderBottom: `1px solid ${borderColor} !important`,
            backgroundColor: `${backgroundColor} !important`,
          }}
        >
          <Box sx={{ display: 'flex !important', alignItems: 'center !important' }}>
            <Avatar
              src={profileData.image || profileData.profileImage}
              sx={{ 
                width: '40px !important', 
                height: '40px !important',
                mr: '8px !important',
                border: isDarkMode 
                  ? '2px solid #4a5568 !important'
                  : `2px solid ${instagramColors.purple} !important`
              }}
            />
            <Box>
              <Box sx={{ display: 'flex !important', alignItems: 'center !important' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{
                  ...styles.instagramUsername,
                  color: `${textColor} !important`
                }}>
                  @{profileData.username}
                </Typography>
                {profileData.isVerified && (
                  <Tooltip title="Verified Account">
                    <VerifiedUserIcon 
                      sx={{ 
                        ...styles.verifiedBadge, 
                        ml: '4px !important',
                        color: isDarkMode ? '#667eea !important' : `${instagramColors.blue} !important`
                      }}
                      fontSize="small" 
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: `${secondaryTextColor} !important` }}>
                {profileData.role}
              </Typography>
            </Box>
          </Box>
          
          <IconButton 
            onClick={onClose}
            size="small"
            edge="end"
            sx={{ color: `${textColor} !important` }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <DialogContent sx={{ p: '0 !important', overflow: 'auto !important' }}>
          <AnimatePresence>
            <MotionDiv
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              key="profile-content" 
            >
              {/* Hero section with overlapping avatar */}
              <Box sx={{ position: 'relative !important' }}>
                {/* Hero background */}
                <Box sx={{ 
                  position: 'relative !important',
                  zIndex: 1,
                  marginBottom: '60px !important'
                }}>
                  <Box 
                    sx={{
                      ...styles.profileCardCover,
                      height: '170px !important',
                      background: profileGradient,
                      position: 'relative !important',
                      overflow: 'hidden !important'
                    }}
                  >
                    <MotionDiv
                      initial={{ scale: 1.2 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.8
                      }}
                    />
                    
                    {/* Action buttons */}
                    <Box
                      sx={{
                        position: 'absolute !important',
                        top: '16px !important',
                        right: '16px !important',
                        display: 'flex !important',
                        gap: '4px !important',
                        zIndex: 2
                      }}
                    >
                      <Tooltip title={copied ? "Link copied!" : "Share profile"}>
                        <IconButton
                          onClick={handleShareProfile}
                          sx={{
                            backgroundColor: isDarkMode 
                              ? 'rgba(30,30,30,0.9) !important'
                              : 'rgba(255,255,255,0.9) !important',
                            color: isDarkMode ? '#ffffff !important' : '#000000 !important',
                            '&:hover': {
                              backgroundColor: isDarkMode 
                                ? 'rgba(30,30,30,1) !important'
                                : 'rgba(255,255,255,1) !important'
                            },
                            boxShadow: '0 3px 8px rgba(0,0,0,0.2) !important',
                          }}
                        >
                          <ShareIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Save profile">
                        <IconButton
                          sx={{
                            backgroundColor: isDarkMode 
                              ? 'rgba(30,30,30,0.9) !important'
                              : 'rgba(255,255,255,0.9) !important',
                            color: isDarkMode ? '#ffffff !important' : '#000000 !important',
                            '&:hover': {
                              backgroundColor: isDarkMode 
                                ? 'rgba(30,30,30,1) !important'
                                : 'rgba(255,255,255,1) !important'
                            },
                            boxShadow: '0 3px 8px rgba(0,0,0,0.2) !important',
                          }}
                        >
                          <BookmarkIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                {/* Overlapping Avatar */}
                <Box 
                  sx={{
                    position: 'absolute !important',
                    left: '50% !important',
                    transform: 'translateX(-50%) !important',
                    top: '90px !important',
                    zIndex: 20
                  }}
                >
                  <MotionDiv
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 100 }}
                  >
                    <Avatar
                      src={profileData.image || profileData.profileImage}
                      alt={`${profileData.firstName} ${profileData.lastName}`}
                      sx={{
                        ...styles.profileCardAvatar,
                        width: '140px !important',
                        height: '140px !important',
                        border: `4px solid ${backgroundColor} !important`,
                        boxShadow: isDarkMode 
                          ? '0 8px 20px rgba(0,0,0,0.4) !important'
                          : '0 8px 20px rgba(0,0,0,0.2) !important',
                      }}
                    />
                  </MotionDiv>
                </Box>

                {/* Profile info section */}
                <Box 
                  sx={{ 
                    px: '12px !important', 
                    pt: '60px !important',
                    pb: '8px !important', 
                    textAlign: 'center !important',
                    backgroundColor: `${backgroundColor} !important`,
                    borderTopLeftRadius: '24px !important',
                    borderTopRightRadius: '24px !important',
                    position: 'relative !important',
                    zIndex: 10,
                    marginTop: '-24px !important'
                  }}
                >
                  <MotionDiv
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Typography 
                      variant="h4" 
                      component="h1" 
                      fontWeight={700}
                      sx={{ 
                        mb: '4px !important',
                        color: `${textColor} !important`,
                        fontSize: { xs: '1.5rem !important', sm: '1.75rem !important' }
                      }}
                    >
                      {`${profileData.firstName} ${profileData.lastName}`}
                    </Typography>
                    
                    <Chip 
                      icon={<EmailIcon fontSize="small" />} 
                      label={profileData.email}
                      sx={{
                        backgroundColor: isDarkMode 
                          ? 'rgba(102, 126, 234, 0.2) !important'
                          : `${instagramColors.lightBlue} !important`,
                        color: isDarkMode 
                          ? '#ffffff !important'
                          : `${instagramColors.darkBlue} !important`,
                        fontWeight: '500 !important',
                        borderRadius: '24px !important',
                        mb: '8px !important',
                        maxWidth: '100% !important',
                        overflow: 'hidden !important',
                        '& .MuiChip-label': {
                          overflow: 'hidden !important',
                          textOverflow: 'ellipsis !important',
                          whiteSpace: 'nowrap !important'
                        }
                      }}
                    />
                    
                    {/* Branch information */}
                    <Chip 
                      icon={<Place fontSize="small" />} 
                      label={profileData.branch || "Main Branch"}
                      sx={{
                        backgroundColor: isDarkMode 
                          ? 'rgba(76, 175, 80, 0.2) !important'
                          : `${alpha(instagramColors.green, 0.15)} !important`,
                        color: isDarkMode 
                          ? '#81c784 !important'
                          : `${instagramColors.green} !important`,
                        fontWeight: '500 !important',
                        borderRadius: '24px !important',
                        mb: '8px !important',
                        maxWidth: '100% !important',
                        '& .MuiChip-label': {
                          overflow: 'hidden !important',
                          textOverflow: 'ellipsis !important',
                          whiteSpace: 'nowrap !important'
                        }
                      }}
                    />
                  </MotionDiv>
                  
                  {/* Quote */}
                  {profileData.quote && (
                    <Card
                      elevation={0}
                      sx={{
                        p: '8px !important',
                        mb: '12px !important',
                        mt: '12px !important',
                        borderRadius: '12px !important',
                        background: isDarkMode 
                          ? 'rgba(255, 255, 255, 0.05) !important'
                          : `${instagramColors.lightGrey} !important`,
                        boxShadow: isDarkMode 
                          ? 'inset 0 0 0 1px rgba(255,255,255,0.1) !important'
                          : 'inset 0 0 0 1px rgba(0,0,0,0.03) !important',
                        position: 'relative !important',
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1) !important' : 'none'
                      }}
                    >
                      <Typography
                        variant="body1"
                        align="center"
                        sx={{
                          fontStyle: 'italic !important',
                          color: `${textColor} !important`, // This fixes the quote text color
                          px: { xs: '4px !important', sm: '12px !important' },
                          position: 'relative !important',
                          fontSize: '0.95rem !important',
                          lineHeight: '1.6 !important',
                          '&::before': {
                            content: '"\\201C"',
                            fontSize: '3rem !important',
                            position: 'absolute !important',
                            top: '-30px !important',
                            left: '-10px !important',
                            color: isDarkMode 
                              ? `${alpha('#667eea', 0.3)} !important`
                              : `${alpha(instagramColors.purple, 0.2)} !important`,
                          },
                          '&::after': {
                            content: '"\\201D"',
                            fontSize: '3rem !important',
                            position: 'absolute !important',
                            bottom: '-50px !important',
                            right: '-10px !important',
                            color: isDarkMode 
                              ? `${alpha('#667eea', 0.3)} !important`
                              : `${alpha(instagramColors.purple, 0.2)} !important`,
                          }
                        }}
                      >
                        {profileData.quote}
                      </Typography>
                    </Card>
                  )}
                  
                  <Divider sx={{ 
                    my: '8px !important',
                    borderColor: `${borderColor} !important`
                  }} />
                  
                  {/* About section */}
                  {profileData.about && (
                    <Box sx={{ my: '12px !important', textAlign: 'left !important' }}>
                      <Typography 
                        variant="body1"
                        sx={{ 
                          ...styles.instagramBio,
                          whiteSpace: 'pre-line !important',
                          lineHeight: '1.8 !important',
                          color: `${textColor} !important`,
                          fontSize: '0.9rem !important'
                        }}
                      >
                        {profileData.about}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              {/* Social Media Links - Theme-aware footer */}
              <Box
                sx={{
                  px: '20px !important',
                  py: '12px !important',
                  mt: '8px !important',
                  borderTop: `1px solid ${borderColor} !important`,
                  backgroundColor: `${backgroundColor} !important`,
                  boxShadow: isDarkMode 
                    ? `0 -4px 20px ${alpha('#000000', 0.2)} !important`
                    : `0 -4px 20px ${alpha(instagramColors.black, 0.03)} !important`,
                  display: 'flex !important',
                  justifyContent: 'center !important',
                  gap: '8px !important',
                  flexWrap: 'wrap !important'
                }}
              >
                <SocialMediaLink
                  url={profileData.linkedin}
                  icon={LinkedInIcon}
                  name="LinkedIn"
                  backgroundColor="#0A66C2"
                />
                <SocialMediaLink
                  url={profileData.facebook}
                  icon={FacebookIcon}
                  name="Facebook"
                  backgroundColor="#1877F2"
                />
                <SocialMediaLink
                  url={profileData.twitter}
                  icon={XIcon}
                  name="X"
                  backgroundColor="#000000"
                />
                <SocialMediaLink
                  url={profileData.instagram}
                  icon={InstagramIcon}
                  name="Instagram"
                  backgroundColor="#E4405F"
                />
              </Box>
            </MotionDiv>
          </AnimatePresence>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default ViewProfile;